import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getExchangeRates,
  putExchangeRates,
  getMarkupConstants,
  putMarkupConstants,
  getMarkupConstantUpdates,
} from '../../shared/api/exchangeRates.api'
import { Loader } from '../../shared/ui/Loader'
import { useAuthStore } from '../auth/authStore'
import { ExchangeRateHistory } from './ExchangeRateHistory'

/** Текст ошибки от API, иначе общее сообщение. */
function apiErrorText(e: unknown): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
    ?.error?.message
  return msg ?? 'Ошибка при сохранении'
}

interface RateField {
  key: 'USD' | 'EUR' | 'CNY'
  label: string
  symbol: string
  color: string
}

const RATE_FIELDS: RateField[] = [
  {
    key: 'USD',
    label: 'Доллар',
    symbol: '$',
    color: 'text-green-700 bg-green-50 border-green-200',
  },
  { key: 'EUR', label: 'Евро', symbol: '€', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  { key: 'CNY', label: 'Юань', symbol: '¥', color: 'text-red-700 bg-red-50 border-red-200' },
]

const MATRIX_COLS: { key: string; label: string }[] = [
  { key: 'STOCK', label: 'Склад' },
  { key: 'CHINA', label: 'Китай' },
  { key: 'RF', label: 'РФ' },
  { key: 'MANUFACTURER', label: 'Производители' },
  { key: 'OTHER', label: 'Стоки' },
]

function parseNum(val: string): number | null {
  const n = parseFloat(val.replace(',', '.'))
  // Number.isFinite отсекает и NaN, и бесконечность («1e400»): раньше она
  // уходила в JSON как null, и сервер отвечал путающим «обязательное поле».
  return !Number.isFinite(n) || n <= 0 ? null : n
}

/** Есть ли в черновике непустое, но нечисловое поле. */
function hasInvalidDraft(draft: Record<string, string>): boolean {
  return Object.values(draft).some((raw) => raw !== '' && parseNum(raw) === null)
}

function formatRateDate(iso: string | null): string {
  if (!iso) return 'не обновлялся'
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function MarkupConstantsBlock() {
  const qc = useQueryClient()
  // Коэффициенты правит только администратор (белый список, как PUT на API).
  // Гендиректор и бухгалтер видят их без правки. Раньше у гендиректора поля и
  // кнопка «Сохранить» были активны, мутацию резал серверный guard, а onError
  // не было: человек жал кнопку и не видел ни результата, ни ошибки.
  const readOnly = useAuthStore((st) => st.user?.role) !== 'ADMIN'

  const { data: constants, isLoading: loadingConstants } = useQuery({
    queryKey: ['markup-constants'],
    queryFn: getMarkupConstants,
  })

  const { data: constantUpdates } = useQuery({
    queryKey: ['markup-constant-updates'],
    queryFn: getMarkupConstantUpdates,
  })

  const [mkDraft, setMkDraft] = useState<Record<string, string>>({})
  const [mkSaved, setMkSaved] = useState(false)

  const mkMutation = useMutation({
    mutationFn: putMarkupConstants,
    onSuccess: (updated) => {
      qc.setQueryData(['markup-constants'], updated)
      // Даты лежат в отдельном кэше — после правки коэффициентов
      // их updatedAt тоже сменился, перезапрашиваем.
      qc.invalidateQueries({ queryKey: ['markup-constant-updates'] })
      setMkDraft({})
      setMkSaved(true)
      setTimeout(() => setMkSaved(false), 2500)
    },
  })

  const handleMkSave = () => {
    const values: Record<string, number> = {}
    for (const col of MATRIX_COLS) {
      const raw = mkDraft[col.key]
      if (raw === undefined || raw === '') continue
      const n = parseNum(raw)
      // Раньше невалидное поле молча выбрасывалось, валидные сохранялись, а
      // черновик очищался целиком — введённое пропадало без сообщения.
      if (n === null) return
      values[col.key] = n
    }
    if (Object.keys(values).length === 0) return
    mkMutation.mutate(values)
  }

  const mkHasChanges = MATRIX_COLS.some(
    (c) => mkDraft[c.key] !== undefined && mkDraft[c.key] !== '',
  )

  if (loadingConstants) return null

  return (
    <>
      <div className="border-t border-gray-200 pt-6">
        <h2 className="text-lg font-semibold text-gray-900">Константы наценки</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Коэффициент наценки для каждой матрицы. Применяется при создании квоты (цена × константа).
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              {MATRIX_COLS.map((col) => (
                <th
                  key={col.key}
                  className="text-center px-3 py-2.5 font-medium text-gray-500 text-xs"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {MATRIX_COLS.map((col) => {
                const current = constants ? constants[col.key] : null
                const raw = mkDraft[col.key] ?? ''
                const parsed = raw !== '' ? parseNum(raw) : null
                const invalid = raw !== '' && parsed === null

                return (
                  <td key={col.key} className="px-3 py-3 text-center">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={raw}
                      placeholder={current != null ? String(current) : '1.4'}
                      disabled={readOnly}
                      onChange={(e) => setMkDraft((d) => ({ ...d, [col.key]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleMkSave()
                      }}
                      className={`w-20 px-2 py-1.5 border rounded-lg text-sm text-center focus:outline-none focus:ring-1 ${
                        invalid
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-200 focus:ring-brand-500'
                      }`}
                    />
                    {current != null && (
                      <div className="text-xs text-gray-400 mt-1">×{current}</div>
                    )}
                    <div className="text-[11px] text-gray-400 mt-1 leading-tight">
                      {formatRateDate(constantUpdates?.[col.key] ?? null)}
                    </div>
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        {readOnly && (
          <span className="text-sm text-gray-500">
            Просмотр: правка коэффициентов доступна администратору
          </span>
        )}
        {!readOnly && (
          <button
            onClick={handleMkSave}
            disabled={!mkHasChanges || hasInvalidDraft(mkDraft) || mkMutation.isPending}
            className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-40 transition-colors inline-flex items-center gap-2"
          >
            {mkMutation.isPending ? (
              <>
                <Loader variant="bus" size={44} />
                Сохранение…
              </>
            ) : (
              'Сохранить константы'
            )}
          </button>
        )}
        {mkSaved && <span className="text-sm text-green-600">Константы обновлены</span>}
        {mkMutation.isError && (
          <span className="text-sm text-red-500">{apiErrorText(mkMutation.error)}</span>
        )}
      </div>
    </>
  )
}

export function ExchangeRatesPage() {
  // Курсы правят администратор и бухгалтер (этап 3, C2) — белый список, как
  // PUT /exchange-rates на API; остальные (гендиректор) — только просмотр.
  const role = useAuthStore((st) => st.user?.role)
  const readOnly = role !== 'ADMIN' && role !== 'ACCOUNTANT'
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: getExchangeRates,
  })

  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  const mutation = useMutation({
    mutationFn: putExchangeRates,
    onSuccess: (updated) => {
      qc.setQueryData(['exchange-rates'], updated)
      // Изменение курса записано в историю — перечитываем её и «курс на момент».
      void qc.invalidateQueries({ queryKey: ['exchange-rate-history'] })
      void qc.invalidateQueries({ queryKey: ['exchange-rates-at'] })
      setDraft({})
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const handleSave = () => {
    const rates: Partial<Record<'USD' | 'EUR' | 'CNY', number>> = {}
    for (const f of RATE_FIELDS) {
      const raw = draft[f.key]
      if (raw === undefined || raw === '') continue
      const n = parseNum(raw)
      if (n === null) return
      rates[f.key] = n
    }
    if (Object.keys(rates).length === 0) return
    mutation.mutate(rates)
  }

  const hasChanges = RATE_FIELDS.some((f) => draft[f.key] !== undefined && draft[f.key] !== '')

  if (isLoading) {
    return <div className="text-sm text-gray-400 p-6">Загрузка...</div>
  }

  return (
    <div className="max-w-2xl w-full space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">Курс валют</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Актуальный курс к рублю. Используется при расчёте сумм в позициях заявок.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {RATE_FIELDS.map((f) => {
          const current = data ? data[f.key] : null
          const raw = draft[f.key] ?? ''
          const parsed = raw !== '' ? parseNum(raw) : null
          const invalid = raw !== '' && parsed === null

          return (
            <div
              key={f.key}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4"
            >
              {/* Значок валюты */}
              <div
                className={`w-10 h-10 rounded-lg border flex items-center justify-center text-lg font-bold shrink-0 ${f.color}`}
              >
                {f.symbol}
              </div>

              {/* Название */}
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800">{f.label}</div>
                {current !== null && (
                  <div className="text-xs text-gray-400">
                    Текущий курс: <span className="font-medium text-gray-600">{current} ₽</span>
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-0.5">
                  Обновлён: {formatRateDate(data?.updatedAtByCurrency?.[f.key] ?? null)}
                </div>
              </div>

              {/* Поле ввода */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <input
                  type="text"
                  inputMode="decimal"
                  value={raw}
                  placeholder={current !== null ? String(current) : '0.00'}
                  disabled={readOnly}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave()
                  }}
                  className={`w-full sm:w-24 px-2 py-1.5 border rounded-lg text-sm text-right focus:outline-none focus:ring-1 ${
                    invalid
                      ? 'border-red-300 focus:ring-red-400'
                      : 'border-gray-200 focus:ring-brand-500'
                  }`}
                />
                <span className="text-sm text-gray-400">₽</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Кнопка сохранения */}
      <div className="flex items-center gap-3">
        {readOnly && (
          <span className="text-sm text-gray-500">
            Просмотр: правка курсов доступна администратору и бухгалтеру
          </span>
        )}
        {!readOnly && (
          <button
            onClick={handleSave}
            disabled={!hasChanges || hasInvalidDraft(draft) || mutation.isPending}
            className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-40 transition-colors inline-flex items-center gap-2"
          >
            {mutation.isPending ? (
              <>
                <Loader variant="bus" size={44} />
                Сохранение…
              </>
            ) : (
              'Сохранить курс'
            )}
          </button>
        )}
        {saved && <span className="text-sm text-green-600">Курс обновлён ✓</span>}
        {mutation.isError && (
          <span className="text-sm text-red-500">{apiErrorText(mutation.error)}</span>
        )}
      </div>

      {/* История курсов и «курс на момент» (этап 3, C2) */}
      <ExchangeRateHistory />

      {/* Блок констант наценки */}
      <MarkupConstantsBlock />
    </div>
  )
}
